import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Lock, User, ArrowRight, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import toast from 'react-hot-toast';

const loginSchema = z.object({
  login: z.string().min(3),
  password: z.string().min(5),
  remember: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { remember: true },
  });

  // In Electron POS mode, cashiers should never see the web login page.
  // If session expired (not authenticated), trigger Electron's native login window.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).electronAPI && !isAuthenticated) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).electronAPI.logout();
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950 text-white">
        <Loader2 className="animate-spin w-8 h-8" />
      </div>
    );
  }

  async function onSubmit(data: LoginFormData) {
    setLoading(true);
    setApiError(null);
    try {
      const user = await login(data);
      toast.success(t('login.success'));

      // Role-based redirection
      const role = user?.role?.toString().trim().toUpperCase();
      if (role === 'FORWARDER') {
        navigate('/agent/dashboard');
      } else if (role === 'SALES_AGENT') {
        navigate('/agent/dashboard');
      } else if (role === 'CASHIER') {
        navigate('/pos/terminal');
      } else {
        navigate('/dashboard');
      }
    } catch (error: unknown) {
      const message = (error as Error).message || t('login.error');
      setApiError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950 flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-500">
      {/* Ambient Background Elements */}
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-blue-600/5 dark:bg-blue-600/10 rounded-full blur-[120px] pointer-events-none animate-pulse" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-slate-400/5 dark:bg-slate-400/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="w-full max-w-[420px] z-10 animate-in fade-in zoom-in-95 duration-700">
        {/* Logo & Branding */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-14 h-14 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-sm flex items-center justify-center mb-6 shadow-xs ring-1 ring-black/5 relative group transition-all duration-300">
            <div className="absolute inset-x-0 -bottom-px h-px bg-linear-to-r from-transparent via-blue-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <Package className="w-7 h-7 text-blue-600 dark:text-blue-500" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2 uppercase text-center">
            {localStorage.getItem('COMPANY_NAME') || 'ERP Uzbekistan'}
          </h1>
          <p className="text-slate-500 dark:text-neutral-400 text-[13px] font-medium tracking-wide uppercase opacity-70">
            {t('login.title')}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-neutral-900/80 backdrop-blur-md border border-slate-200 dark:border-neutral-800 rounded-sm shadow-xl p-8 relative overflow-hidden">
          {/* Top Accent Line */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-blue-600" />

          {apiError && (
            <div className="mb-6 p-4 rounded-sm bg-red-50 dark:bg-red-500/5 border border-red-200 dark:border-red-500/20 flex items-start gap-3 animate-in slide-in-from-top-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-500 shrink-0 mt-0.5" />
              <p className="text-[13px] text-red-700 dark:text-red-400 font-semibold">{apiError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-500 dark:text-neutral-500 uppercase tracking-wider ml-1">
                {t('login.label_login')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <User className="h-4 w-4" />
                </div>
                <input
                  type="text"
                  data-testid="login-input"
                  {...register('login')}
                  className={`w-full bg-slate-50 dark:bg-neutral-950/50 border ${errors.login ? 'border-red-500' : 'border-slate-200 dark:border-neutral-800'} text-slate-900 dark:text-white text-sm rounded-sm block pl-10 p-3 outline-hidden focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500 transition-all placeholder:text-slate-400 dark:placeholder:text-neutral-600`}
                  placeholder={t('login.placeholder_login')}
                  autoComplete="username"
                />
              </div>
              {errors.login && <p className="text-[11px] text-red-500 font-medium ml-1 mt-1">{errors.login.message}</p>}
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center px-1">
                <label className="text-[11px] font-bold text-slate-500 dark:text-neutral-500 uppercase tracking-wider">
                  {t('login.label_password')}
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  data-testid="password-input"
                  {...register('password')}
                  className={`w-full bg-slate-50 dark:bg-neutral-950/50 border ${errors.password ? 'border-red-500' : 'border-slate-200 dark:border-neutral-800'} text-slate-900 dark:text-white text-sm rounded-sm block pl-10 pr-10 p-3 outline-hidden focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500 transition-all placeholder:text-slate-400 dark:placeholder:text-neutral-600`}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-blue-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-[11px] text-red-500 font-medium ml-1 mt-1">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between px-1">
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    {...register('remember')}
                    className="peer w-4 h-4 rounded-sm border-slate-300 dark:border-neutral-700 text-blue-600 focus:ring-transparent dark:bg-neutral-950 cursor-pointer appearance-none checked:bg-blue-600 checked:border-blue-600 transition-all"
                  />
                  <div className="absolute inset-0 flex items-center justify-center text-white opacity-0 peer-checked:opacity-100 pointer-events-none">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <span className="text-[12px] font-medium text-slate-600 dark:text-neutral-400 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                  {t('login.remember')}
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3.5 px-4 rounded-sm text-[12px] font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-blue-500/20 active:bg-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed group uppercase tracking-widest mt-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {t('login.submit')}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Industrial Footer */}
        <div className="mt-10 pt-8 border-t border-slate-200 dark:border-neutral-800 flex flex-col items-center gap-4">
          <p className="text-center text-[11px] font-semibold text-slate-400 dark:text-neutral-500 uppercase tracking-widest leading-relaxed max-w-xs">
            {t('login.footer')}
          </p>
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-neutral-900 rounded-sm border border-slate-200 dark:border-neutral-800">
            <Package className="w-3.5 h-3.5 text-blue-600/50" />
            <span className="text-[10px] font-bold text-slate-500 dark:text-neutral-400 uppercase tracking-wider">
              {t('login.admin_contact')}: <span className="text-blue-600 dark:text-blue-500">+998905009082</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
